<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Campos personalizados</h1>
    </div>

    <wz-table-container>
      <wz-table-toolbar>
        <template #left>
          <div style="min-width: 240px;">
            <wz-select v-model="filterEntity" :items="entityOptions" defaultOption="Todas las entidades" @change="loadData" />
          </div>
        </template>
        <template #right>
          <wz-tooltip text="Recargar">
            <wz-button variant="outline" @click="loadData()"><i class="ri-loop-right-line"></i></wz-button>
          </wz-tooltip>
          <wz-button @click="openNew">Nuevo campo</wz-button>
        </template>
      </wz-table-toolbar>

      <p class="text-muted text-sm px-4 pt-2">
        Los campos que cree aquí aparecen solos en el formulario de su entidad y se pueden usar en las plantillas con el
        marcador que se indica. No requieren despliegue.
      </p>

      <wz-table>
        <thead>
          <tr>
            <th>Entidad</th>
            <th>Etiqueta</th>
            <th>Marcador</th>
            <th>Tipo</th>
            <th>Empresa</th>
            <th style="text-align: center;">Obligatorio</th>
            <th style="text-align: right;">Valores</th>
            <th>Estado</th>
            <th>Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="list.length === 0">
            <td colspan="9" class="text-center text-muted py-4">No hay campos personalizados.</td>
          </tr>
          <tr v-for="f in list" :key="f.id" :class="{ 'cf-inactive': f.status !== 1 }">
            <td>{{ entityLabel(f.entity) }}</td>
            <td>{{ f.label }}</td>
            <td><code>{{ f.placeholder }}</code></td>
            <td>{{ typeLabel(f.fieldType) }}</td>
            <td>{{ f.companyName || 'Todas' }}</td>
            <td style="text-align: center;">{{ f.isRequired ? 'Sí' : 'No' }}</td>
            <td style="text-align: right;">{{ f.valuesCount }}</td>
            <td>
              <wz-badge :variant="f.status === 1 ? 'success-solid' : 'warning-solid'" pill>
                {{ f.status === 1 ? 'ACTIVO' : 'INACTIVO' }}
              </wz-badge>
            </td>
            <td style="text-align: center;">
              <wz-dropdown :items="rowActions(f)" placement="left" @click="onAction($event, f)" />
            </td>
          </tr>
        </tbody>
      </wz-table>

      <wz-table-footer><span>{{ list.length }} campo(s)</span></wz-table-footer>
    </wz-table-container>

    <wz-modal :value="modal.open" :title="modal.id ? 'Editar campo personalizado' : 'Nuevo campo personalizado'" size="md"
      @input="modal.open = $event">
      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/2 mb-3 px-2">
          <label class="font-medium">Entidad <span class="text-danger">*</span></label>
          <wz-select v-model="form.entity" :items="entityOptions" defaultOption="Seleccionar" :disabled="!!modal.id" />
        </div>
        <div class="w-full md:w-1/2 mb-3 px-2">
          <label class="font-medium">Empresa</label>
          <wz-select v-model="form.companyId" :items="companyOptions" defaultOption="Todas las empresas" :disabled="!!modal.id" />
        </div>
        <div class="w-full md:w-1/2 mb-3 px-2">
          <wz-input label="Código" required v-model="form.code" :disabled="!!modal.id" placeholder="transportAllowancePerDay" />
          <small class="text-muted">Letra inicial, solo letras y números. Marcador: <code>{{ previewPlaceholder }}</code></small>
        </div>
        <div class="w-full md:w-1/2 mb-3 px-2">
          <wz-input label="Etiqueta (la ve RRHH)" required v-model="form.label" maxlength="150" />
        </div>
        <div class="w-full md:w-1/2 mb-3 px-2">
          <label class="font-medium">Tipo <span class="text-danger">*</span></label>
          <wz-select v-model="form.fieldType" :items="typeOptions" defaultOption="Seleccionar" :disabled="typeLocked" />
          <small v-if="typeLocked" class="text-muted">No se puede cambiar el tipo: el campo ya tiene valores cargados.</small>
        </div>
        <div class="w-full md:w-1/2 mb-3 px-2">
          <wz-input label="Orden" type="number" v-model.number="form.displayOrder" />
        </div>
        <div v-if="form.fieldType === 'LISTA'" class="w-full mb-3 px-2">
          <label class="font-medium">Opciones (una por línea)</label>
          <wz-textarea v-model="form.optionsText" rows="4" />
        </div>
        <div class="w-full mb-3 px-2">
          <label class="flex items-center"><input type="checkbox" class="mr-2" v-model="form.isRequired" /> Obligatorio</label>
          <small class="text-muted">Si es obligatorio, un documento que lo use no se genera mientras falte el dato.</small>
        </div>
      </div>
      <template #footer>
        <wz-button variant="outline-secondary" @click="modal.open = false">Cancelar</wz-button>
        <wz-button variant="primary" :disabled="saving" @click="save">Guardar</wz-button>
      </template>
    </wz-modal>
  </div>
</template>

<script>
import { Alert } from '@wizepe/wizeui'
import { CustomFieldService, CompanyService } from '@/core/services'

const ENTITIES = [
  { key: 'COLABORADOR', label: 'Colaborador', prefix: 'employee' },
  { key: 'CONTRATO',    label: 'Contrato',    prefix: 'contract' },
  { key: 'EMPRESA',     label: 'Empresa',     prefix: 'company' },
  { key: 'CARGO',       label: 'Cargo',       prefix: 'position' },
  { key: 'AREA',        label: 'Área',        prefix: 'area' },
  { key: 'SEDE',        label: 'Sede',        prefix: 'site' },
]
const TYPES = [
  { key: 'TEXTO', label: 'Texto' }, { key: 'NUMERO', label: 'Número' }, { key: 'MONTO', label: 'Monto' },
  { key: 'FECHA', label: 'Fecha' }, { key: 'LISTA', label: 'Lista' }, { key: 'BOOLEANO', label: 'Sí / No' },
]
const emptyForm = () => ({ entity: null, companyId: null, code: '', label: '', fieldType: null, optionsText: '', isRequired: false, displayOrder: 0 })

export default {
  name: 'CustomFieldList',
  data() {
    return {
      list: [], companies: [], filterEntity: null, saving: false,
      entityOptions: ENTITIES.map(e => ({ key: e.key, label: e.label })),
      typeOptions: TYPES,
      modal: { open: false, id: null, valuesCount: 0 },
      form: emptyForm(),
    }
  },
  computed: {
    companyOptions() { return this.companies.map(c => ({ key: c.id, label: c.name })) },
    typeLocked() { return !!this.modal.id && this.modal.valuesCount > 0 },
    previewPlaceholder() {
      const e = ENTITIES.find(x => x.key === this.form.entity)
      return e && this.form.code ? '${' + e.prefix + '.' + this.form.code + '}' : '${…}'
    },
  },
  methods: {
    entityLabel(k) { const e = ENTITIES.find(x => x.key === k); return e ? e.label : k },
    typeLabel(k) { const t = TYPES.find(x => x.key === k); return t ? t.label : k },
    rowActions(f) {
      return [{ key: 'edit', label: 'Editar' }, { key: 'toggle', label: f.status === 1 ? 'Desactivar' : 'Activar' }]
    },
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const resp = await CustomFieldService.list(this.filterEntity || undefined)
        this.list = resp.payload || []
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar los campos personalizados')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    openNew() {
      this.form = emptyForm()
      this.modal = { open: true, id: null, valuesCount: 0 }
    },
    async onAction(action, f) {
      const key = action && action.key ? action.key : action
      if (key === 'edit') {
        this.form = {
          entity: f.entity, companyId: f.companyId, code: f.code, label: f.label, fieldType: f.fieldType,
          optionsText: (f.options || []).join('\n'), isRequired: !!f.isRequired, displayOrder: f.displayOrder || 0,
        }
        this.modal = { open: true, id: f.id, valuesCount: f.valuesCount || 0 }
      } else if (key === 'toggle') {
        try {
          await CustomFieldService.changeStatus(f.id, f.status === 1 ? 0 : 1)
          Alert.success(f.status === 1 ? 'Campo desactivado (sus valores se conservan)' : 'Campo activado')
          await this.loadData()
        } catch (e) {
          console.error(e)
          Alert.error(e.errors ? e.errors.message : 'No se pudo cambiar el estado')
        }
      }
    },
    async save() {
      const options = (this.form.optionsText || '').split('\n').map(o => o.trim()).filter(Boolean)
      const data = {
        entity: this.form.entity, companyId: this.form.companyId || null, code: (this.form.code || '').trim(),
        label: this.form.label, fieldType: this.form.fieldType, options,
        isRequired: this.form.isRequired, displayOrder: this.form.displayOrder || 0,
      }
      this.saving = true
      try {
        if (this.modal.id) await CustomFieldService.update(this.modal.id, data)
        else await CustomFieldService.create(data)
        Alert.success('Campo guardado')
        this.modal.open = false
        await this.loadData()
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'No se pudo guardar el campo')
      } finally {
        this.saving = false
      }
    },
  },
  async created() {
    await this.loadData()
    try { this.companies = (await CompanyService.listActive()).payload || [] } catch (e) { console.error(e) }
  },
}
</script>

<style lang="stylus">
.cf-inactive td
  color #9ca3af
</style>
